import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, Shield, Plus, Trash2, History, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function RolesSettings() {
  const [catalog, setCatalog] = useState({ groups: [], valid_roles: [], roles_meta: {}, available_home_routes: [] });
  const [rolesMap, setRolesMap] = useState({});
  const [homeRoutes, setHomeRoutes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNewRole, setShowNewRole] = useState(false);
  const [newRole, setNewRole] = useState({ key: "", label: "", description: "" });
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const { refreshPolicies } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, r] = await Promise.all([
        api.get("/settings/policies-catalog"),
        api.get("/settings/roles"),
      ]);
      setCatalog(cat.data);
      setRolesMap(r.data.roles || {});
      setHomeRoutes(r.data.home_routes || {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const { data } = await api.get("/settings/roles/audit-log?limit=100");
      setAuditEntries(data.entries || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => { if (showAuditLog) loadAudit(); }, [showAuditLog, loadAudit]);

  const rolesMeta = catalog.roles_meta || {};
  const validRoles = catalog.valid_roles || [];

  const isChecked = (role, policyKey) => {
    if (role === "admin") return true;
    const pols = rolesMap[role] || [];
    return pols.includes("*") || pols.includes(policyKey);
  };

  const toggle = (role, policyKey) => {
    if (role === "admin") return;
    setRolesMap((rm) => {
      const cur = rm[role] || [];
      const has = cur.includes(policyKey);
      return { ...rm, [role]: has ? cur.filter((p) => p !== policyKey) : [...cur, policyKey] };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...rolesMap, admin: ["*"] };
      await api.put("/settings/roles", { roles: payload, home_routes: homeRoutes });
      toast.success("Políticas guardadas");
      await refreshPolicies();
      if (showAuditLog) loadAudit();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const createRole = async () => {
    if (!newRole.key.trim() || !newRole.label.trim()) {
      return toast.error("Clave y nombre son obligatorios");
    }
    try {
      await api.post("/settings/roles/custom", {
        key: newRole.key.trim().toLowerCase(),
        label: newRole.label.trim(),
        description: newRole.description.trim(),
        policies: [],
      });
      toast.success(`Rol '${newRole.label}' creado`);
      setShowNewRole(false);
      setNewRole({ key: "", label: "", description: "" });
      await load();
      if (showAuditLog) loadAudit();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteRole = async (key, label) => {
    if (!window.confirm(`¿Eliminar el rol personalizado '${label}'? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/settings/roles/custom/${encodeURIComponent(key)}`);
      toast.success("Rol eliminado");
      await load();
      if (showAuditLog) loadAudit();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return <PageContainer><p className="p-6">Cargando…</p></PageContainer>;

  return (
    <PageContainer>
      <PageHeader
        title="Roles y Políticas"
        subtitle="Configura qué puede hacer cada rol. Administrador tiene todos los permisos."
        testid="roles-settings-header"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowNewRole(true)} variant="outline" data-testid="roles-new-btn">
              <Plus className="w-4 h-4 mr-1" /> Nuevo rol
            </Button>
            <Button onClick={save} disabled={saving} className="bg-primary font-bold" data-testid="roles-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </div>
        }
      />

      <div className="bg-card border rounded-md max-h-[calc(100vh-13rem)] overflow-auto">
        <table className="w-full text-sm" data-testid="roles-matrix">
          <thead className="sticky top-0 z-20 bg-card shadow-[0_1px_0_0_hsl(var(--border))]">
            <tr className="bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/70">
              <th className="p-2 text-left overline w-[30%] bg-inherit">Política</th>
              {validRoles.map((r) => {
                const meta = rolesMeta[r] || {};
                const isCustom = meta.is_system === false;
                return (
                  <th key={r} className="p-2 text-center overline bg-inherit">
                    <div className="flex flex-col items-center gap-0.5">
                      <Shield className={`w-4 h-4 ${isCustom ? "text-primary" : "text-muted-foreground"}`} />
                      <span className="text-xs">{meta.label || r}</span>
                      {isCustom && (
                        <button
                          onClick={() => deleteRole(r, meta.label || r)}
                          className="text-[10px] text-red-500 hover:underline flex items-center gap-0.5"
                          title="Eliminar rol personalizado"
                          data-testid={`roles-delete-${r}`}
                        >
                          <Trash2 className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
            {/* Iter 58.4 — Selector de página inicial por rol.
                Se usa cuando el rol no tiene `dashboard.view`. */}
            <tr className="bg-muted/80 backdrop-blur supports-[backdrop-filter]:bg-muted/50 border-t border-border/60">
              <th className="p-2 text-left align-middle bg-inherit">
                <div className="overline text-[10px]">Página inicial</div>
                <div className="text-[10px] font-normal normal-case tracking-normal text-muted-foreground">
                  Se usa cuando el rol no tiene acceso al Dashboard
                </div>
              </th>
              {validRoles.map((r) => (
                <th key={r} className="p-1 bg-inherit">
                  <select
                    value={homeRoutes[r] || "/"}
                    disabled={r === "admin"}
                    onChange={(e) =>
                      setHomeRoutes((h) => ({ ...h, [r]: e.target.value }))
                    }
                    className="h-7 text-[11px] border border-border rounded px-1 bg-background w-full font-normal normal-case tracking-normal disabled:opacity-50"
                    data-testid={`role-home-${r}`}
                    title="Página a la que el usuario entra al iniciar sesión si no tiene Dashboard"
                  >
                    {(catalog.available_home_routes || []).map((opt) => (
                      <option key={opt.route} value={opt.route}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(catalog.groups || []).map((g) => (
              <React.Fragment key={g.name}>
                <tr className="bg-muted/30">
                  <td colSpan={validRoles.length + 1} className="p-2 font-bold text-xs uppercase text-primary tracking-wider">
                    {g.name}
                  </td>
                </tr>
                {g.policies.map((p) => (
                  <tr key={p.key} className="border-t hover:bg-accent/30">
                    <td className="p-2">
                      <div className="font-medium">{p.desc}</div>
                      <div className="text-[10px] font-mono text-muted-foreground">{p.key}</div>
                    </td>
                    {validRoles.map((r) => (
                      <td key={r} className="p-2 text-center">
                        <Checkbox
                          checked={isChecked(r, p.key)}
                          onCheckedChange={() => toggle(r, p.key)}
                          disabled={r === "admin"}
                          data-testid={`policy-${r}-${p.key}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground mt-3">
        💡 Los cambios se aplican inmediatamente a los usuarios cuando recargan la página o vuelven a iniciar sesión.
      </p>

      {/* Panel de auditoría colapsable */}
      <div className="mt-6 bg-card border rounded-md" data-testid="audit-panel">
        <button
          onClick={() => setShowAuditLog((s) => !s)}
          className="w-full p-3 flex items-center justify-between text-left hover:bg-accent/30"
          data-testid="audit-toggle"
        >
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-primary" />
            <span className="font-semibold">Historial de cambios</span>
            <span className="text-xs text-muted-foreground">({auditEntries.length})</span>
          </div>
          {showAuditLog ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showAuditLog && (
          <div className="border-t max-h-[420px] overflow-y-auto">
            {auditLoading && <p className="p-4 text-sm text-muted-foreground">Cargando…</p>}
            {!auditLoading && auditEntries.length === 0 && (
              <p className="p-4 text-sm text-muted-foreground">No hay cambios registrados.</p>
            )}
            {auditEntries.map((e, idx) => {
              const color = { created: "text-emerald-600", updated: "text-blue-600", deleted: "text-red-600" }[e.action] || "";
              return (
                <div key={idx} className="p-3 border-b text-xs" data-testid={`audit-entry-${idx}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono">{(e.at || "").slice(0, 19).replace("T", " ")}</span>
                    <span className={`font-bold uppercase ${color}`}>{e.action}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-muted-foreground">Rol:</span>
                    <span className="font-semibold">{e.role}</span>
                    <span className="text-muted-foreground">Por:</span>
                    <span>{e.by_email}</span>
                    {e.label && <span className="text-primary">· {e.label}</span>}
                  </div>
                  {(e.added && e.added.length > 0) && (
                    <div><span className="text-emerald-600 font-bold">+ </span>{e.added.join(", ")}</div>
                  )}
                  {(e.removed && e.removed.length > 0) && (
                    <div><span className="text-red-600 font-bold">− </span>{e.removed.join(", ")}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialog crear rol */}
      <Dialog open={showNewRole} onOpenChange={setShowNewRole}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear rol personalizado</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">Clave (identificador único) *</Label>
              <Input
                value={newRole.key}
                onChange={(e) => setNewRole((r) => ({ ...r, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") }))}
                placeholder="ej: supervisor_ventas"
                className="mt-1 font-mono"
                data-testid="new-role-key"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Solo minúsculas, dígitos y guiones bajos. Empieza con letra.</p>
            </div>
            <div>
              <Label className="overline">Nombre visible *</Label>
              <Input
                value={newRole.label}
                onChange={(e) => setNewRole((r) => ({ ...r, label: e.target.value }))}
                placeholder="ej: Supervisor de Ventas"
                className="mt-1"
                data-testid="new-role-label"
              />
            </div>
            <div>
              <Label className="overline">Descripción</Label>
              <Input
                value={newRole.description}
                onChange={(e) => setNewRole((r) => ({ ...r, description: e.target.value }))}
                placeholder="Descripción breve del rol"
                className="mt-1"
                data-testid="new-role-description"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              El rol se creará sin políticas. Podrás asignárselas en la matriz después de crearlo.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRole(false)}>Cancelar</Button>
            <Button onClick={createRole} className="bg-primary font-bold" data-testid="new-role-confirm">Crear rol</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
